import { isEmpty, isPresent } from '@ember/utils';
import { Data, TransformsList } from 'cio/utils/analytics/transforms';
import {
  TYPE_FILTERS as SEGMENT_TYPE_FILTERS,
  USAGE_FILTERS as SEGMENT_USAGE_FILTERS,
} from 'cio/pods/segments/index/controller';
import { NAMES, SYNC_STATUSES } from 'cio/utils/ad-audiences';
import SegmentModel, { SegmentType } from 'cio/models/segment';
import { generateFilteredTimeFrameData } from 'cio/utils/analytics/transforms/metrics';
import { getModelEntryNameFromId } from 'cio/utils/analytics/transforms/utils';

export enum JsonConditionEditedInterface {
  Basic = 'basic',
  Advanced = 'advanced',
}

export enum JsonConditionEditType {
  Add = 'added',
  Delete = 'deleted',
  Edit = 'edited',
}

const JSON_CONDITION_INTERFACE_NAME = {
  [JsonConditionEditedInterface.Basic]: 'Basic',
  [JsonConditionEditedInterface.Advanced]: 'Advanced',
};

const JSON_CONDITION_EDIT_TYPE_NAME = {
  [JsonConditionEditType.Add]: 'Added',
  [JsonConditionEditType.Delete]: 'Deleted',
  [JsonConditionEditType.Edit]: 'Edited',
};

const SEGMENT_TYPE_NAMES = {
  static: 'Manual',
  dynamic: 'Data-driven',
  dynamicForm: 'Data-driven (Form)',
};

function safeParseArray(json: string): unknown[] {
  try {
    let parsed = JSON.parse(json);
    return parsed instanceof Array ? parsed : [];
  } catch {
    return [];
  }
}

function flattenConditions(segment: SegmentModel): [] {
  // @ts-ignore: conditions not typed yet
  if (!segment.conditions?.and) {
    return [];
  }

  // @ts-ignore: conditions not typed yet
  return segment.conditions.and.reduce((acc: any, condition: any) => {
    let conditions = [];

    if (condition.and) {
      conditions = [...condition.and];
    } else if (condition.or) {
      conditions = [...condition.or];
    } else {
      conditions = [condition];
    }

    return [...acc, ...conditions];
  }, []);
}

function generateBaseSegmentData(data: Data & { model?: SegmentModel }) {
  if (data.model == null) {
    return {};
  }

  let segmentType = data.model.isLinkedToForm ? SEGMENT_TYPE_NAMES.dynamicForm : SEGMENT_TYPE_NAMES[data.model.type];

  let attrs: Record<string, string | number> = {
    'Segment ID': data.model.id,
    'Segment Type': segmentType,
  };

  if (!data.model.isManual) {
    attrs['Number Of Segment Conditions'] = flattenConditions(data.model).length;
  }

  return attrs;
}

const Transforms: TransformsList = {
  base(data: Data & { model: SegmentModel }) {
    return generateBaseSegmentData(data);
  },

  editStarted(data: Data & { model: SegmentModel }) {
    let attrs: Record<string, string | number> = generateBaseSegmentData(data);

    // This event can be triggered by a route change or a button click. If it's a route
    // change, that means the edit was for segment criteria.
    if (data.route) {
      attrs['Segment Edit Type'] = 'Criteria';
    } else {
      attrs['Segment Edit Type'] = {
        name: 'Name',
        description: 'Description',
      }[data.type as 'name' | 'description'];
    }

    return attrs;
  },

  type(data: { type: SegmentType }) {
    return {
      'Segment Type': SEGMENT_TYPE_NAMES[data.type],
    };
  },

  listDetails(data) {
    let { queryParams } = data.route!;
    let usage = SEGMENT_USAGE_FILTERS.findBy('value', queryParams.usage);
    let type = SEGMENT_TYPE_FILTERS.findBy('value', queryParams.type);

    // By the time the transition is done, the query params are serialized back into a string.
    // So we have to parse them again here to be able to map.
    let audienceTypes;
    if (typeof queryParams.audienceTypes === 'string') {
      audienceTypes = safeParseArray(queryParams.audienceTypes);
      audienceTypes = audienceTypes.map((type: string) => (NAMES as any)[type]);
    }

    let audienceStatuses;
    if (typeof queryParams.audienceStatuses === 'string') {
      audienceStatuses = safeParseArray(queryParams.audienceStatuses);
      audienceStatuses = audienceStatuses.map((type: string) => (SYNC_STATUSES as any)[type].label);
    }

    let tags: false | string[] = false;
    let objectTypes: false | string[] = false;

    if (data.route != null && data.model != null) {
      tags = getModelEntryNameFromId(data.route.queryParams.searchTagIds, data.model.tags);
      objectTypes = getModelEntryNameFromId(data.route.queryParams.objectTypeIds, data.model.objectTypes);
    }

    return {
      'Segment Status': queryParams.archived ? 'Archived' : 'Active',
      'Filtered by Segment Usage': usage ? usage.label : 'All Usage',
      'Filtered by Segment Type': type ? type.label : 'All Types',
      'Filtered By Segment Search': isPresent(queryParams.search),
      'Filtered by Ad Audience Network': isEmpty(audienceTypes) ? undefined : audienceTypes,
      'Filtered By Ad Audience Sync Status': isEmpty(audienceStatuses) ? undefined : audienceStatuses,
      ...(tags !== false && { 'Filtered By Campaign Tag(s)': tags }),
      ...(objectTypes !== false && { 'Filtered by Object Types': objectTypes }),
    };
  },

  creationStarted(data: Data & { source?: 'campaign_trigger' | 'campaign_filter' | 'newsletter' }) {
    if (!data.source) {
      return {};
    }

    return {
      Source: {
        campaign_trigger: 'Campaign Trigger',
        campaign_filter: 'Campaign Filter',
        newsletter: 'Newsletter',
        triggered_broadcast: 'API Triggered Broadcast',
      }[data.source],
    };
  },

  segmentMembershipTimeframeChanged(data: Data & { model: SegmentModel }) {
    return {
      'Segment ID': data.model.id,
      'Segment Type': SEGMENT_TYPE_NAMES[data.model.type],
      ...generateFilteredTimeFrameData(data),
    };
  },

  peopleTabViewed(data: Data & { model: SegmentModel }) {
    let analyticsData = {
      ...generateBaseSegmentData(data),
    };

    if (data.source) {
      analyticsData.Source = (
        {
          dashboard: 'Dashboard',
          membership_chart: 'Segment Membership Chart',
          people_tab: 'Segment People Tab',
        } as Record<string, string>
      )[data.source];
    }

    return analyticsData;
  },

  chartHover(data: Data & { model?: SegmentModel }) {
    return {
      'Segment ID': data.model?.id,
      'Time of Hover': data.time,
    };
  },

  conditionSelected(data: Data) {
    let attrs: Record<string, string | number> = {
      Type: data.conditionType,
      Value: data.conditionValue,
    };

    let { route } = data;
    if (route) {
      attrs.Source =
        {
          'campaigns.campaign.setup.trigger.index': 'Campaign Trigger',
          'segments.segment.overview.edit': 'Segment Page',
          'broadcasts.newsletter.setup.recipients.index': 'Newsletter',
          'broadcasts.broadcast.setup.recipients.index': 'API Triggered Broadcast',
        }[route.name] ?? '';

      let findRouteAttribute = (name: string) => route?.find((r) => r.paramNames.includes(name))?.params[name];
      let segmentID = findRouteAttribute('segment_id');
      if (segmentID) {
        attrs['Segment ID'] = `${findRouteAttribute('environment_id')}seg${segmentID}`;
      }
    }

    return attrs;
  },

  documentationViewed(data: Data) {
    return {
      'Segment ID': data.model?.id,
      'Banner Dismissed': data.bannerDismissed ?? false,
      'Documentation Link Clicked': data.docsLinksClicked ?? false,
    };
  },

  jsonConditionEdited(data: Data & { model?: SegmentModel }) {
    return {
      ...generateBaseSegmentData(data),
      'JSON Interface Used': JSON_CONDITION_INTERFACE_NAME[data.interface as JsonConditionEditedInterface],
      'JSON Condition Edit Type': JSON_CONDITION_EDIT_TYPE_NAME[data.editType as JsonConditionEditType],
    };
  },

  backfillStarted(data: Data) {
    return {
      'Campaigns Affected': data.campaignsAffected,
    };
  },

  backfillCompleted(data: Data) {
    return {
      'Match Selection': data.matchSelection,
      'Campaigns Affected': data.campaignsAffected,
    };
  },
};

export default Transforms;
